import { defaultImage } from 'data/DefaultImages/DefaultArticleImage';
import {
  Author,
  ArticleRichImage,
  UpNextArticle,
  SingleArticlePrimaryImage,
  SingleArticleCategoryTopic,
  SingleArticleAuthorTopic,
  SingleArticle,
  SingleArticleMetadataTopic,
  Metadata,
  SingleArticleRelatedArticleImage,
  Category,
  SlugTitle,
} from './types';
import {
  SingleArticleQuery_articleCollection_items_integrationCategoriesCollection_items,
  SingleArticleQuery_articleCollection_items_personasCollection_items,
  SingleArticleQuery_articleCollection_items_integrationsMentionedCollection_items,
  SingleArticleQuery_articleCollection_items_tagsCollection_items,
} from 'graphql/__generated__/operations/SingleArticleQuery';
import { UpNextArticleQuery_upNextArticle_items_primaryImage } from 'graphql/__generated__/operations/UpNextArticleQuery';

export const relatedArticleImage = (
  image:
    | SingleArticleRelatedArticleImage
    | UpNextArticleQuery_upNextArticle_items_primaryImage
    | null
    | undefined,
): ArticleRichImage => ({
  alt: image?.describedImage ?? '',
  url: image?.image?.url ?? defaultImage(),
  title: image?.image?.title ?? '',
  maxWidth: image?.maxWidth ?? null,
});

export const articleCategories = (
  categories: (SingleArticleCategoryTopic | null)[],
): Category[] =>
  categories.map((item) => {
    return {
      categoryName: item?.title ?? '',
      parentCategory: {
        name: item?.parentCategory?.title ?? '',
        slug:
          item?.parentCategory?.linkedFrom?.categoryAssemblyCollection?.items[0]
            ?.slug ?? '',
      },
      categoryAssemblySlug:
        item?.linkedFrom?.categoryAssemblyCollection?.items[0]?.slug ?? '',
    };
  });

export const articleAuthors = (
  authors: (SingleArticleAuthorTopic | null)[],
): Author[] =>
  authors.map((author) => {
    return {
      bio: author?.biographyShort ?? '',
      name: author?.displayName ?? '',
      email: author?.email ?? '',
    };
  });

export const articleImage = (
  image: SingleArticlePrimaryImage | null,
): ArticleRichImage => {
  if (!image) {
    return {
      url: '',
      alt: '',
      title: '',
      caption: {},
      maxWidth: null,
    };
  }

  return {
    alt: image.describedImage ?? '',
    caption: image.caption,
    // if url === '' img elem won't be rendered
    url: image.image?.url ?? '',
    title: image.image?.title ?? '',
    maxWidth: image?.maxWidth ?? null,
  };
};

/**
 * Get all related articles in an Article
 */
export const articleRelatedArticles = (
  relatedArticles: (SingleArticle | null)[],
): UpNextArticle[] => {
  if (!relatedArticles.length) {
    return [];
  }

  return relatedArticles.map((relatedArticle) => {
    return {
      category:
        // At this point, we only ever need to display a single category on the related article, so we're grabbing the first one here.
        /**
         * @TODO We should return the child category. Not the first one.
         */
        relatedArticle?.categoriesCollection?.items[0]?.title ?? '',
      slug: relatedArticle?.slug ?? '',
      shortDescription: relatedArticle?.shortDescription ?? '',
      title: relatedArticle?.title ?? '',
      image: relatedArticleImage(relatedArticle?.primaryImage),
    };
  });
};

export const articleMetadata = (
  metadata: SingleArticleMetadataTopic | null | undefined,
): Metadata => {
  return {
    seoTitle: metadata?.seoTitle ?? '',
    seoDescription: metadata?.seoDescription ?? '',
    socialImage: metadata?.socialImage || null,
  };
};

export const integrationsMentioned = (
  integrations: SingleArticleQuery_articleCollection_items_integrationsMentionedCollection_items[],
): SlugTitle[] =>
  integrations.map((integration) => {
    return {
      slug: integration?.slug as string,
      title: integration?.title as string,
    };
  });

export const articleTags = (
  tags: SingleArticleQuery_articleCollection_items_tagsCollection_items[],
): SlugTitle[] =>
  tags.map((tag) => ({
    title: tag?.title as string,
    slug: tag?.slug as string,
  }));

export function integrationCategories(
  integrations: SingleArticleQuery_articleCollection_items_integrationCategoriesCollection_items[],
): string[] {
  return integrations.map((integration) => integration.slug as string);
}

export function personas(
  personas: SingleArticleQuery_articleCollection_items_personasCollection_items[],
): string[] {
  return personas.map((persona) => persona.slug as string);
}
